import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { filterFactory } from "@similarweb/pro-logic-components";
import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useIndustryAdsEntityFilter } from "app/pages/digital-marketing/ad-intelligence/common/ads/industry/useIndustryAdsFilterValues";

import { createFilterEmptyState, renderFaviconItem, toEntityOptions } from "../../shared/filters/entity-filter-helpers";
import { createMultiSelectLabel } from "../../shared/filters/label-helpers";

interface IIndustryCampaignsFilterState {
    advertisers?: string[];
}

const Filters = filterFactory.createTypedFilters<IIndustryCampaignsFilterState>();

interface IIndustryCampaignsFiltersProps {
    category: IFlattenedCategory;
    selectedAdvertisers: string[];
    onAdvertisersFilterChange: (advertisers: string[]) => void;
}

export const IndustryCampaignsFilters = ({
    category,
    selectedAdvertisers,
    onAdvertisersFilterChange,
}: IIndustryCampaignsFiltersProps) => {
    const { translate } = useTranslate();

    const advertiserFilter = useIndustryAdsEntityFilter(category, "advertisers");

    const getEmptyState = createFilterEmptyState(
        translate("ad.intelligence.verification.filters.loading"),
        translate("dropdown.no.results"),
    );

    const advertiserOptions: SelectOption[] = useMemo(
        () => toEntityOptions(advertiserFilter.values, selectedAdvertisers, Boolean(advertiserFilter.searchTerm)),
        [advertiserFilter.values, selectedAdvertisers, advertiserFilter.searchTerm],
    );

    const handleFilterChange = (newState: IIndustryCampaignsFilterState) => {
        onAdvertisersFilterChange(newState.advertisers ?? []);
        advertiserFilter.setSearchTerm("");
    };

    return (
        <Filters.Provider
            state={{ advertisers: selectedAdvertisers }}
            onChange={handleFilterChange}
        >
            <Filters.MultiSelect
                name="advertisers"
                label={createMultiSelectLabel(
                    translate("ad.intelligence.ads.table.advertiser.title"),
                    (id) => advertiserOptions.find((option) => option.id === id),
                    (option) => option.label,
                )}
                tooltip={translate("ad.intelligence.ads.widget.filters.advertiser.tooltip")}
                withSearch
                searchValue={advertiserFilter.searchTerm}
                onSearch={advertiserFilter.setSearchTerm}
                isLoading={advertiserFilter.isSearchPending}
                isDisabled={advertiserFilter.isInitialLoading}
                emptyState={getEmptyState(advertiserFilter.isSearchPending, advertiserOptions)}
                options={advertiserOptions}
                renderItem={renderFaviconItem}
                placeholder={translate("ad.intelligence.ads.widget.filters.advertiser.placeholder")}
                width="320px"
                data-automation="industry-campaigns-advertisers-filter"
            />
        </Filters.Provider>
    );
};
